import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AI_PDF_CONST from "../../util/PdfAiConst";
import { fetchTenants } from "../../api/tenantApi";
import { fetchTenantServices } from "../../api/tenantServiceApi";
import PageHeader from "../../components/admin/PageHeader";
import LoadingState from "../../components/admin/LoadingState";
import ErrorState from "../../components/admin/ErrorState";

const ServiceListPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const tenantRes = await fetchTenants();
        const tenantList = Array.isArray(tenantRes)
          ? tenantRes
          : tenantRes?.data?.items || tenantRes?.data || [];

        const enriched = await Promise.all(
          tenantList.map(async (tenant) => {
            let services = [];
            try {
              const svcRes = await fetchTenantServices(tenant.id);
              services = Array.isArray(svcRes)
                ? svcRes
                : svcRes?.data?.items || svcRes?.data || [];
            } catch {
              // skip
            }
            const enabled = services.filter((s) => s.status === "enabled");
            return {
              ...tenant,
              totalServices: services.length,
              enabledServices: enabled.length,
              enabledNames: enabled.map((s) => s.service_key),
            };
          })
        );
        setRows(enriched);
      } catch (err) {
        setError(err.message || "Failed to load services data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <LoadingState label="Loading services data..." />;
  if (error) return <ErrorState description={error} />;

  const manageLink = (id) => AI_PDF_CONST.ROUTE.ADMIN_TENANT_DETAIL.replace(":tenant_id", id) + "?tab=services";

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Platform admin"
        title="Services"
        description="Overview of AI services across all tenants."
      />

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Tenant Name</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Enabled Services</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Total Services</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 text-right">Quick Manage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((tenant) => (
              <tr key={tenant.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-5 py-4 font-medium text-slate-900">{tenant.name}</td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap gap-1">
                    {tenant.enabledNames.length > 0 ? tenant.enabledNames.map((name) => (
                      <span key={name} className="inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">{name}</span>
                    )) : (
                      <span className="text-xs text-slate-400 italic">None enabled</span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-4 text-slate-700">{tenant.enabledServices}/{tenant.totalServices}</td>
                <td className="px-5 py-4 text-right">
                  <Link
                    to={manageLink(tenant.id)}
                    className="inline-flex items-center rounded-lg bg-[#096dea] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#0959bb]"
                  >
                    Manage →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No tenants found.</div>
        ) : null}
      </div>
    </div>
  );
};

export default ServiceListPage;
